import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { JournalPostSummary, Product } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { getCategoryCounts, getFeaturedProducts } from '@/lib/data/products'
import { getPosts } from '@/lib/data/journal'
import MaskReveal from '@/components/motion/mask-reveal'
import Hero from '@/components/home/hero'
import TapeCross from '@/components/home/tape-cross'
import Manifesto from '@/components/home/manifesto'
import TheDrop from '@/components/home/the-drop'
import CampaignTrail from '@/components/home/campaign-trail'
import SplitLookbook from '@/components/home/split-lookbook'
import Materials from '@/components/home/materials'
import Categories from '@/components/home/categories'
import JournalTeaser from '@/components/home/journal-teaser'
import Newsletter from '@/components/home/newsletter'
import { editorial } from '@/constants/media'

interface HomeProps {
    featured: Array<Product>
    posts: Array<JournalPostSummary>
    counts: Record<string, number>
}

const Home: FC<HomeProps> = ({ featured = [], posts = [], counts = {} }): ReactNode => {
    return (
        <Layout>
            <Seo />

            <Hero />
            <TapeCross />
            <Manifesto />
            <TheDrop products={featured} />
            <CampaignTrail />
            <SplitLookbook />

            <MaskReveal
                id='soft-armor'
                words={['Soft', 'Armor']}
                caption='Chapter III — Heavy knits, quiet shapes'
                label='(04) — Chapter III'
                image={editorial.softArmorRibPortrait}
                alt="A model resting her chin on her hand in a chunky ribbed knit"
                shape='pill'
                focus='object-[50%_60%]'
            />

            <Materials />
            <Categories counts={counts} />
            <JournalTeaser posts={posts} />
            <Newsletter />
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async () => {
    try {
        const [featured, posts, counts] = await Promise.all([
            getFeaturedProducts(6),
            getPosts(3),
            getCategoryCounts()
        ])

        return { props: { featured, posts, counts } }
    } catch (error) {
        console.error('Home page content could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { featured: [], posts: [], counts: {} } }
    }
}

export default Home
